import { Wallet, RefreshCcw } from "lucide-react";
import {
  Mint,
  useGetBalanceQuery,
  useGetTransientTopupsQuery,
} from "../generated/graphql";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { useEffect, useState } from "react";
import { Label } from "./ui/label";
import { useToast } from "./ui/use-toast";
import { useAuth } from "../AuthProvider";
import axios from "axios";
import { motion } from "framer-motion";
import { formatNumberToString } from "../lib/formatNumbers";

interface BalanceCardProps {
  mintOptions?: Mint[];
}

interface ExchangeRates {
  [key: string]: number;
}

export function BalanceCard({ mintOptions }: BalanceCardProps) {
  const { organizationId } = useAuth();
  const { toast } = useToast();
  const [
    { data: balanceData, error: balanceError, fetching: balanceFetching },
    refetchBalance,
  ] = useGetBalanceQuery({
    variables: { organizationId: organizationId || "" },
    requestPolicy: "network-only",
    pause: organizationId === "" || !organizationId,
  });

  const [exchangeRates, setExchangeRates] = useState<ExchangeRates>({});

  const [
    { data: transientTopupsData, error: transientTopupsError },
    refetchTransientTopups,
  ] = useGetTransientTopupsQuery({
    variables: { organizationId: organizationId || "" },
    requestPolicy: "network-only",
    pause: organizationId === "" || !organizationId,
  });
  const transientTopups = transientTopupsData?.getTransientTopups;

  useEffect(() => {
    if (balanceError) {
      toast({
        title: "Error",
        description: balanceError.message,
        variant: "destructive",
      });
    }

    if (transientTopupsError) {
      toast({
        title: "Error",
        description: transientTopupsError.message,
        variant: "destructive",
      });
    }
  }, [balanceError, toast, transientTopupsError]);

  useEffect(() => {
    if (balanceFetching) {
      return;
    }
    const timerId = setTimeout(() => {
      refetchBalance({ requestPolicy: "network-only" });
      refetchTransientTopups({ requestPolicy: "network-only" });
    }, 20 * 1000);

    return () => clearTimeout(timerId);
  }, [balanceFetching, refetchBalance, refetchTransientTopups]);

  const balances = balanceData?.getBalance;

  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        const response = await axios.get(
          "https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum&vs_currencies=usd"
        );
        setExchangeRates({
          BTC: response.data.bitcoin.usd,
          ETH: response.data.ethereum.usd,
        });
      } catch (error) {
        console.error("Failed to fetch exchange rates:", error);
      }
    };

    fetchExchangeRates();
    const interval = setInterval(fetchExchangeRates, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  const calculateUsdValue = (credit: number, mint: string) => {
    if (mint === "USDT" || mint === "DAI") return credit.toFixed(2) + " USD";
    const rate = exchangeRates[mint.toUpperCase()];
    if (!rate) return "N/A";
    return (
      (credit * rate).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      }) + " USD"
    );
  };

  const formatCryptoAmount = (amount: number, mint: string) => {
    if (mint === "ETH" || mint === "DAI") {
      // check if amount is in scientific notation
      if (amount.toString().includes("e")) {
        return (amount * 1e-18).toFixed(6); // Convert from wei to ETH/DAI
      }
    }
    return amount.toString();
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg font-semibold flex items-center">
          Balances
          <Wallet className="ml-2 h-4 w-4 opacity-50" />
        </CardTitle>

        <span>
          <Button
            style={{
              maxWidth: "100px",
            }}
            size="sm"
            variant="outline"
            onClick={() => {
              refetchBalance({ requestPolicy: "network-only" });
              refetchTransientTopups({ requestPolicy: "network-only" });
              toast({
                title: "Refreshed",
                description: "Balances have been refreshed",
                variant: "default",
              });
            }}
          >
            <RefreshCcw className="h-4 w-4" />
          </Button>
        </span>
      </CardHeader>
      <CardContent style={{ paddingTop: "10px" }}>
        {(balances?.length ?? 0) === 0 &&
        (transientTopups?.length ?? 0) === 0 ? (
          <div>
            <Label>No balances available</Label>
          </div>
        ) : (
          <div className="space-y-4">
            {balances?.map((b, idx) => {
              const mintOption = mintOptions?.find((m) => m?.id === b?.mintId);
              return (
                <motion.div
                  key={idx}
                  className="flex flex-row items-center justify-between rounded-md p-4 bg-[#fbfbfb] "
                >
                  {mintOption && (
                    <div className="flex items-center mb-2 sm:mb-0">
                      <img
                        alt=""
                        src={mintOption.img ?? ""}
                        className="h-8 w-8 mr-3"
                      />
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500">
                          {mintOption.chain}
                        </span>
                        <span className="text-sm font-bold">
                          {mintOption.name}
                        </span>
                      </div>
                    </div>
                  )}
                  <span className="font-semibold text-center mb-2 sm:mb-0">
                    {formatCryptoAmount(Number(b?.credit) || 0, b?.mint || "")}{" "}
                    {b?.mint}
                    <span className="block text-gray-400 text-xs">
                      ~
                      {calculateUsdValue(Number(b?.credit) || 0, b?.mint || "")}{" "}
                    </span>
                  </span>
                </motion.div>
              );
            })}
          </div>
        )}

        {transientTopups && (
          <div className="mt-4 space-y-4">
            {transientTopups.map((t, idx) => {
              const mintOption = mintOptions?.find(
                (m) => m?.id === t?.mint?.id
              );

              return (
                <div style={{ position: "relative" }}>
                  {t?.decisionAt === null ? (
                    <span
                      style={{
                        position: "absolute",
                        top: "-10px",
                        right: "-5px",
                        zIndex: 10,
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "white",
                        backgroundColor: "red",
                        padding: "2px 6px",
                        borderRadius: "5px",
                      }}
                    >
                      Pending Approval
                    </span>
                  ) : (
                    <span
                      style={{
                        position: "absolute",
                        top: "-10px",
                        right: "-5px",
                        zIndex: 10,
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "white",
                        backgroundColor: "#ffa500",
                        padding: "2px 6px",
                        borderRadius: "5px",
                      }}
                    >
                      Sending to LP
                    </span>
                  )}

                  <motion.div
                    key={idx}
                    className="flex flex-row items-center justify-between rounded-md p-4 bg-[#fff9ee] relative overflow-hidden"
                  >
                    <motion.div
                      className="absolute bottom-0 left-0 h-1 bg-[#f5ead7]"
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{
                        duration: 15,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    />
                    {mintOption && (
                      <div className="flex items-center mb-2 sm:mb-0">
                        <img
                          alt=""
                          src={mintOption.img ?? ""}
                          className="h-8 w-8 mr-3"
                        />
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-500">
                            {mintOption.chain}
                          </span>
                          <span className="text-sm font-bold">
                            {mintOption.name}
                          </span>
                        </div>
                      </div>
                    )}
                    <span
                      className="font-semibold justify-self-center mb-2 sm:mb-0"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      {formatNumberToString(
                        formatCryptoAmount(
                          Number(t?.amount) || 0,
                          mintOption?.symbol || ""
                        )
                      )}{" "}
                      {mintOption?.symbol}
                      <span className="text-gray-400 text-xs justify-self-end">
                        ~
                        {formatNumberToString(
                          calculateUsdValue(
                            Number(t?.amount) || 0,
                            mintOption?.symbol || ""
                          )
                        )}{" "}
                      </span>
                    </span>
                  </motion.div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
